/**
 * Two locales, no library. scope.md §10.
 *
 * A key-union dictionary: `MessageKey` is every key of the English dictionary, and every
 * other locale is a `Record<MessageKey, string>`, so a missing translation will not
 * compile. The source stays UK English; only what players read is translated.
 */

import { en } from './en.js';
import { es } from './es.js';

export type Locale = 'en-GB' | 'es-ES';
export type MessageKey = keyof typeof en;
export type Dictionary = Record<MessageKey, string>;
export type Params = Record<string, string | number>;
export type Translate = (key: MessageKey, params?: Params) => string;

export const DEFAULT_LOCALE: Locale = 'en-GB';

/** In the language itself, so it can be found by someone who cannot read the other. */
export const LOCALES: readonly { id: Locale; name: string }[] = [
  { id: 'en-GB', name: 'English' },
  { id: 'es-ES', name: 'Español' },
];

const DICTIONARIES: Record<Locale, Dictionary> = { 'en-GB': en, 'es-ES': es };

export function isLocale(value: unknown): value is Locale {
  return value === 'en-GB' || value === 'es-ES';
}

export function translate(locale: Locale, key: MessageKey, params?: Params): string {
  const text: string = DICTIONARIES[locale][key] ?? en[key];
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in params ? String(params[name]) : whole));
}

export function translator(locale: Locale): Translate {
  return (key, params) => translate(locale, key, params);
}

/** Keys that come as a `.one` / `.other` pair. */
export type PluralKey = {
  [K in MessageKey]: K extends `${infer Base}.one` ? Base : never
}[MessageKey];

/** Both locales pluralise the same simple way: one, or anything else. */
export function plural(t: Translate, key: PluralKey, count: number, params?: Params): string {
  const form = (count === 1 ? `${key}.one` : `${key}.other`) as MessageKey;
  return t(form, { count, ...params });
}

/** "5 seconds", "1 segundo". */
export function secondsText(t: Translate, ms: number): string {
  return plural(t, 'seconds', ms / 1000);
}

/** Enough of a contract to describe it, without depending on the engine. */
export type ContractShape = {
  requirements: readonly { kind: 'set' | 'run'; length: number }[];
  useWholeHand: boolean;
};

/** "1 set of 3 and 1 run of 4", in the reader's language. */
export function describeContractIn(t: Translate, contract: ContractShape): string {
  const { requirements, useWholeHand } = contract;
  if (useWholeHand) {
    return t('contract.wholeHand', { count: requirements.length, length: requirements[0]?.length ?? 4 });
  }
  const counts = new Map<string, { kind: 'set' | 'run'; length: number; count: number }>();
  for (const r of requirements) {
    const key = `${r.kind}:${r.length}`;
    const entry = counts.get(key) ?? { kind: r.kind, length: r.length, count: 0 };
    entry.count += 1;
    counts.set(key, entry);
  }
  return [...counts.values()]
    .map((c) => plural(t, c.kind === 'set' ? 'contract.set' : 'contract.run', c.count, { length: c.length }))
    .join(t('contract.and'));
}

export { en, es };
